<template>
  <el-drawer :visible.sync="visible" direction="rtl" size="50%" :with-header="false" :wrapperClosable="false"
    custom-class="inbound-exception-drawer" @opened="onDrawerOpened">
    <div class="form-title">
      <h2>{{ title }}</h2>
      <button class="link" type="button" @click="close">×</button>
    </div>
    <el-form ref="form" :model="form" :rules="rules" label-position="top" class="exception-form-body">
      <div class="form-grid">
        <el-form-item class="form-full" label="异常类型" prop="exceptionType">
          <div class="scene-radio" :class="{ disabled: isEdit }">
            <label class="scene-radio-item" :class="{ active: isType(EXCEPTION_TYPE.PRODUCT) }"
              @click="selectExceptionType(EXCEPTION_TYPE.PRODUCT)">
              <span class="scene-radio-dot"></span>
              异常商品
            </label>
            <label class="scene-radio-item" :class="{ active: isType(EXCEPTION_TYPE.PARCEL) }"
              @click="selectExceptionType(EXCEPTION_TYPE.PARCEL)">
              <span class="scene-radio-dot"></span>
              异常包裹
            </label>
          </div>
        </el-form-item>
        <el-form-item class="form-full" :label="codeLabel" prop="bizCode">
          <el-input ref="bizCodeInput" v-model="form.bizCode" class="scan-input" :placeholder="codePlaceholder"
            maxlength="128" />
        </el-form-item>
        <el-form-item label="登记仓库" prop="warehouse">
          <el-select v-model="form.warehouse" placeholder="请选择仓库" style="width: 100%">
            <el-option v-for="item in warehouseOptions" :key="item.wareCode" :label="item.wareName"
              :value="item.wareCode" />
          </el-select>
        </el-form-item>
        <el-form-item label="登记人" prop="registerName">
          <el-input v-model="form.registerName" maxlength="50" placeholder="默认龙荷英，可修改" />
        </el-form-item>
        <el-form-item label="发注书名" prop="purchaseBook">
          <el-input v-model="form.purchaseBook" maxlength="100" />
        </el-form-item>
        <el-form-item label="需回复人" prop="replyUserMobile">
          <el-select v-model="form.replyUserMobile" placeholder="请选择" style="width: 100%" @change="onReplyUserChange">
            <el-option v-for="item in displayReplyUsers" :key="item.mobile" :label="item.userName"
              :value="item.mobile" />
          </el-select>
        </el-form-item>
        <el-form-item class="form-full" label="异常说明" prop="exceptionDesc">
          <el-input type="textarea" v-model="form.exceptionDesc" :rows="4" maxlength="1000" />
        </el-form-item>
        <el-form-item class="form-full" label="异常照片">
          <PhotoUpload ref="photoUpload" />
        </el-form-item>
      </div>
    </el-form>
    <div class="form-foot">
      <el-button @click="close">取消</el-button>
      <el-button type="success" :loading="saving" @click="saveForm">保存</el-button>
    </div>
  </el-drawer>
</template>

<script>
import PhotoUpload from "./PhotoUpload";
import { inboundExceptionSave, inboundExceptionEdit } from "@/apis/inHouse";
import { DEFAULT_REGISTER_NAME, DEFAULT_WAREHOUSE_OPTIONS, EXCEPTION_TYPE } from "../data";

export default {
  name: "ExceptionFormDrawer",
  components: {
    PhotoUpload,
  },
  props: {
    // 需回复人，页面从接口拉取后传入
    replyUserOptions: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },
  data() {
    return {
      // 抽屉显示
      visible: false,
      // new / edit
      mode: "new",
      // 保存中
      saving: false,
      // 编辑回显时接口名单没有的人
      extraReplyUsers: [],
      // 异常类型枚举，模板里切换商品/包裹
      EXCEPTION_TYPE,
      // 表单数据
      form: this.emptyForm(),
      // 校验规则
      rules: {
        exceptionType: [{ required: true, message: "请选择异常类型", trigger: "change" }],
        bizCode: [{ required: true, message: "请先扫描或输入商品条码 / 运单号", trigger: "blur" }],
        warehouse: [{ required: true, message: "请选择登记仓库", trigger: "change" }],
        registerName: [{ required: true, message: "请填写登记人", trigger: "blur" }],
        purchaseBook: [{ required: true, message: "请填写发注书名", trigger: "blur" }],
        replyUserMobile: [{ required: true, message: "请选择需回复人", trigger: "change" }],
        exceptionDesc: [{ required: true, message: "请填写异常说明", trigger: "blur" }],
      },
    };
  },
  computed: {
    // 抽屉标题
    title: function () {
      return this.isEdit ? "编辑异常" : "新增异常";
    },
    // 编辑态
    isEdit: function () {
      return this.mode === "edit";
    },
    // 编码字段标签
    codeLabel: function () {
      return this.isType(EXCEPTION_TYPE.PARCEL) ? "包裹条码 / 运单号" : "商品条码 / SKU";
    },
    // 编码字段占位
    codePlaceholder: function () {
      return this.isType(EXCEPTION_TYPE.PARCEL) ? "请扫描包裹运单号" : "请扫描商品条码";
    },
    // 杭州仓、西子仓
    warehouseOptions: function () {
      const list = this.$store.getters.getWarehouses || [];
      const filtered = list.filter((item) => item.wareName === "杭州仓");
      return filtered.length ? filtered : DEFAULT_WAREHOUSE_OPTIONS;
    },
    // 接口名单 + 历史回显补项
    displayReplyUsers: function () {
      return this.replyUserOptions.concat(this.extraReplyUsers);
    },
  },
  methods: {
    // 空表单
    emptyForm: function () {
      return {
        exceptionCode: "",
        exceptionType: EXCEPTION_TYPE.PRODUCT,
        bizCode: "",
        warehouse: "",
        registerName: DEFAULT_REGISTER_NAME,
        purchaseBook: "",
        replyUserId: "",
        replyUserName: "",
        replyUserMobile: "",
        exceptionDesc: "",
        picRelation: "",
      };
    },
    // 打开抽屉
    open: function (mode, record) {
      this.mode = mode;
      this.visible = true;
      this.extraReplyUsers = [];
      this.form = this.emptyForm();
      if (record) {
        this.form.exceptionType = this.normalizeExceptionType(record.exceptionType);
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
        this.fillDefaultWarehouse();
        if (this.isEdit && record) {
          this.form = {
            exceptionCode: record.exceptionCode,
            exceptionType: this.normalizeExceptionType(record.exceptionType),
            bizCode: record.bizCode,
            warehouse: record.warehouse,
            registerName: record.registerName,
            purchaseBook: record.purchaseBook,
            replyUserId: this.normalizeReplyUserId(record.replyUserId),
            replyUserName: record.replyUserName,
            replyUserMobile: record.replyUserMobile || "",
            exceptionDesc: record.exceptionDesc,
            picRelation: record.picRelation,
          };
          this.fillReplyUserOption(this.form.replyUserMobile, this.form.replyUserId, this.form.replyUserName);
          this.$refs.photoUpload.initExist(record.picRelation);
          if (!this.form.warehouse) {
            this.fillDefaultWarehouse();
          }
        } else {
          // 新增默认登记人按配置填写，允许在本页修改
          this.form.registerName = DEFAULT_REGISTER_NAME;
          this.fillDefaultReplyUser();
          this.$refs.photoUpload.initNew();
        }
      });
    },
    // 抽屉动画结束后再聚焦，避免打开瞬间抢不到输入框
    onDrawerOpened: function () {
      if (!this.isEdit) {
        this.focusBizCodeInput();
      }
    },
    // 新增时把光标放到条码输入框，方便扫码
    focusBizCodeInput: function () {
      this.$nextTick(() => {
        const input = this.$refs.bizCodeInput;
        if (input && typeof input.focus === "function") {
          input.focus();
        }
      });
    },
    // 接口可能返回字符串，统一成数字后再比较选中态
    normalizeExceptionType: function (value) {
      const numericType = Number(value);
      if (numericType === EXCEPTION_TYPE.PARCEL) {
        return EXCEPTION_TYPE.PARCEL;
      }
      return EXCEPTION_TYPE.PRODUCT;
    },
    // 当前是否某类异常
    isType: function (type) {
      return Number(this.form.exceptionType) === Number(type);
    },
    // 新增可切换类型，编辑保持原类型
    selectExceptionType: function (type) {
      if (this.isEdit) {
        return;
      }
      this.form.exceptionType = type;
      this.focusBizCodeInput();
    },
    // 关闭抽屉，不保存
    close: function () {
      this.visible = false;
    },
    // 默认杭州仓，否则取当前仓
    fillDefaultWarehouse: function () {
      const hangzhou = this.warehouseOptions.find((item) => item.wareName === "杭州仓");
      const current = this.warehouseOptions.find((item) => item.wareCode === this.$store.getters.getWareCode);
      this.form.warehouse = (hangzhou || current || this.warehouseOptions[0] || {}).wareCode || "";
    },
    // 新增默认选中第一个需回复人
    fillDefaultReplyUser: function () {
      const first = this.displayReplyUsers[0];
      if (!first) {
        return;
      }
      this.form.replyUserId = first.userId;
      this.form.replyUserName = first.userName;
      this.form.replyUserMobile = first.mobile;
    },
    // 需回复人 ID 统一成数字，避免接口字符串对不上选项
    normalizeReplyUserId: function (userId) {
      if (userId === null || userId === undefined || userId === "") {
        return "";
      }
      const numericId = Number(userId);
      return Number.isNaN(numericId) ? userId : numericId;
    },
    // 编辑回显时优先按手机号匹配
    fillReplyUserOption: function (mobile, userId, userName) {
      const matched = this.displayReplyUsers.find((item) => item.mobile && item.mobile === mobile)
        || this.displayReplyUsers.find((item) => String(item.userId) === String(userId))
        || this.displayReplyUsers.find((item) => item.userName === userName);
      if (matched) {
        this.form.replyUserId = matched.userId;
        this.form.replyUserName = matched.userName;
        this.form.replyUserMobile = matched.mobile;
        return;
      }
      if (!mobile && !userName) {
        return;
      }
      this.extraReplyUsers = [
        { userId: userId, userName: userName || mobile, mobile: mobile },
      ];
      this.form.replyUserMobile = mobile;
    },
    // 同步需回复人姓名和ID
    onReplyUserChange: function (mobile) {
      const user = this.displayReplyUsers.find((item) => item.mobile === mobile);
      this.form.replyUserId = user ? user.userId : "";
      this.form.replyUserName = user ? user.userName : "";
      this.form.replyUserMobile = user ? user.mobile : mobile;
    },
    // 保存
    saveForm: function () {
      this.form.bizCode = (this.form.bizCode || "").trim();
      if (!this.form.bizCode) {
        this.$message.warning("请先扫描或输入商品条码 / 运单号");
        return;
      }
      this.$refs.form.validate((valid) => {
        if (!valid) {
          return;
        }
        const warehouse = this.warehouseOptions.find((item) => item.wareCode === this.form.warehouse);
        const params = {
          ...this.form,
          bizCode: this.form.bizCode.trim(),
          registerName: (this.form.registerName || "").trim(),
          purchaseBook: (this.form.purchaseBook || "").trim(),
          exceptionDesc: (this.form.exceptionDesc || "").trim(),
          warehouseName: warehouse ? warehouse.wareName : "",
          picRelation: this.$refs.photoUpload.getPicRelation(),
        };
        this.saving = true;
        const request = this.isEdit ? inboundExceptionEdit : inboundExceptionSave;
        request(params)
          .then((res) => {
            if (res.code === 0) {
              this.$message.success("保存成功");
              this.close();
              this.$emit("confirm");
            }
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
  },
};
</script>

<style>
.inbound-exception-drawer {
  min-width: 560px;
  text-align: left;
}

.inbound-exception-drawer .el-drawer__body {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
}
</style>

<style scoped>
.form-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e7ea;
  padding: 18px 24px 14px;
  flex-shrink: 0;
}

.form-title h2 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: #27313b;
}

.link {
  border: 0;
  background: none;
  font-size: 24px;
  line-height: 1;
  color: #71808a;
  cursor: pointer;
  padding: 0 4px;
}

.exception-form-body {
  padding: 18px 24px 24px;
  overflow: auto;
  flex: 1;
}

.exception-form-body>>>.el-form-item {
  margin-bottom: 0;
}

.exception-form-body>>>.el-form-item__label {
  float: none;
  display: block;
  width: auto !important;
  text-align: left !important;
  padding: 0 0 6px;
  line-height: 1.4;
  color: #56636c;
  font-weight: normal;
}

.exception-form-body>>>.el-form-item__content {
  margin-left: 0 !important;
  line-height: normal;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
}

.form-full {
  grid-column: 1 / -1;
}

.scan-input>>>.el-input__inner {
  height: 48px;
  font-size: 16px;
}

.scene-radio {
  display: flex;
  gap: 12px;
}

.scene-radio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border: 1px solid #cbd4da;
  background: #f5f7f8;
  cursor: pointer;
  font-weight: 600;
  color: #27313b;
  margin: 0;
}

.scene-radio-item.active {
  border-color: #36aa78;
  background: #e8f5ed;
  color: #238357;
  box-shadow: inset 0 -3px #36aa78;
}

.scene-radio-dot {
  width: 17px;
  height: 17px;
  border: 1px solid #b5c0c7;
  border-radius: 50%;
  box-sizing: border-box;
  background: #fff;
  position: relative;
}

.scene-radio-item.active .scene-radio-dot {
  border-color: #36aa78;
}

.scene-radio-item.active .scene-radio-dot::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #36aa78;
}

.scene-radio.disabled {
  pointer-events: none;
}

.scene-radio.disabled .scene-radio-item {
  cursor: not-allowed;
  opacity: 0.72;
}

.form-foot {
  flex-shrink: 0;
  border-top: 1px solid #e2e7ea;
  padding: 14px 24px;
  text-align: right;
  background: #fff;
}
</style>
